import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import type { OneOffChannel, SerializedAssetPublish, SlotChannelGroup } from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { slotBadgeText, type PublishBadgeTone } from './publishBadgeText';
/* eslint-disable css-modules/no-unused-class -- the badge row uses its own slice */
import styles from './Publish.module.scss';
/* eslint-enable css-modules/no-unused-class */

type BadgeEntry = { channel: OneOffChannel; record: SerializedAssetPublish | null };

interface PublishBadgesProps<E extends BadgeEntry> {
  /** One group per channel of the slot (fym-shared `groupSlotChannels`). */
  groups: ReadonlyArray<SlotChannelGroup<E>>;
  /** False for the demo artist and a read-only artist: the badges show, the click does nothing. */
  canAct: boolean;
  onOpen: (group: SlotChannelGroup<E>) => void;
}

const TONE_CLASS: Record<PublishBadgeTone, string | undefined> = {
  on: styles.badgeOn,
  off: styles.badgeOff,
  pending: styles.badgePending,
  problem: styles.badgeProblem,
  waits: styles.badgeWaits,
};

/**
 * The one-off channel badges of a Kit slot (owner request 2026-10-09: "binary 'is it published
 * there'"): ONE badge per channel, also when the slot holds several files (owner fix 2026-10-10:
 * the four thumbnails said "Not set on YouTube" four times). With several files the badge counts
 * them ("Set on YouTube · 2 of 4"). A click opens the publish dialog. Nothing for no channel.
 */
function PublishBadgesComponent<E extends BadgeEntry>({
  groups,
  canAct,
  onOpen,
}: PublishBadgesProps<E>): React.ReactElement | null {
  const intl = useIntl();
  if (groups.length === 0) {
    return null;
  }
  return (
    <div className={styles.badgeRow} data-testid="publish-badges">
      {groups.map((group) => {
        const badge = slotBadgeText(intl, group);
        return (
          <button
            key={group.channel}
            type="button"
            className={clsx(styles.badge, TONE_CLASS[badge.tone])}
            disabled={!canAct}
            onClick={() => onOpen(group)}
            data-testid={`publish-badge-${group.channel}`}
          >
            {badge.tone === 'on' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
            {badge.text}
          </button>
        );
      })}
    </div>
  );
}

export const PublishBadges = TypedMemo(PublishBadgesComponent);
